import EditIcon from '@mui/icons-material/Edit'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import {
  Box,
  Button,
  Card,
  CardHeader,
  Dialog,
  Divider,
  IconButton,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import dayjs from 'dayjs'
import { useState } from 'react'
import CircularLoading from '../../../../../components/CircularProgress'
import DistrubuteChannelTag from '../../../../../components/DistrubuteChannelTag'
import OrderTag from '../../../../../components/OrderTag'
import { PropertyList } from '../../../../../components/PropertyList'
import { PropertyListItem } from '../../../../../components/PropertyListItem'
import { DATE_FORMAT } from '../../../../../constants/global'
import { IDistributionChannel } from '../../../../../types/distribution-channel'
import {
  EOrderStatus,
  EOrderType,
  IOrder,
  IOrderItem,
  OrderShippingProvider,
} from '../../../../../types/order'
import { OrderEditDistributionFormModal } from './OrderEditDistributionFormModal'
import { OrderEditPaymentFormModal } from './OrderEditPaymentFormModal'
import OverdueTag from '../../../../../components/OrderTag/overdue'
import TrackingProviderImage from '../../../../../components/TrackingProviderImage'
import { isEmpty } from 'lodash'

export interface IOderDetailCard {
  status?: EOrderStatus
  quantity?: number
  commission?: number
  paymentMethod?: EOrderType
  items?: IOrderItem[]
  actor?: string
  distribution?: IDistributionChannel
  createdAt?: string
  updatedAt?: string
  note?: string
}

interface FilterGroupProps {
  isLoading: boolean
  data?: IOderDetailCard | any
  order: IOrder
  onSuccess: () => void
}

const OrderDetailCard = ({
  isLoading,
  data,
  order,
  onSuccess,
}: FilterGroupProps) => {
  const [isModalEditPayment, setIsModalEditPayment] = useState<boolean>(false)
  const [isModalEditDistribution, setIsModalEditDistribution] =
    useState<boolean>(false)
  const [slipOpen, setSlipOpen] = useState(false)

  const editable =
    [EOrderStatus.PENDING, EOrderStatus.TELESALE, EOrderStatus.AI].includes(
      order?.status as EOrderStatus,
    ) && order?.trackingCode === null

  return (
    <>
      <OrderEditPaymentFormModal
        isOpen={isModalEditPayment}
        onClose={setIsModalEditPayment}
        order={order}
        onSuccess={onSuccess}
      />
      <OrderEditDistributionFormModal
        isOpen={isModalEditDistribution}
        onClose={setIsModalEditDistribution}
        order={order}
        onSuccess={onSuccess}
      />
      <CircularLoading isLoading={isLoading} />
      <Card>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: 2,
          }}
        >
          <CardHeader title="รายละเอียด" />
          {editable && (
            <Button
              startIcon={<EditIcon />}
              color="warning"
              size="medium"
              variant="text"
              onClick={() => setIsModalEditPayment(!isModalEditPayment)}
            >
              แก้ไขการชำระเงิน
            </Button>
          )}
        </Box>
        <Divider />
        <PropertyList>
          <PropertyListItem
            align={`horizontal`}
            label="รหัสใบสั่งซื้อ"
            value={data?.code ?? null}
          />
          <Divider />
          <PropertyListItem align={`horizontal`} label="สถานะ">
            {data?.status && <OrderTag status={data?.status as EOrderStatus} />}
            <OverdueTag order={order} />
          </PropertyListItem>
          <PropertyListItem align={`horizontal`} label="หมายเลขพัสดุ">
            {data?.trackingCode && data?.trackingProvider && (
              <Box mb={1}>
                <TrackingProviderImage
                  trackingProvider={
                    order.trackingProvider as OrderShippingProvider
                  }
                />
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  textAlign={'center'}
                >
                  {data.trackingCode}
                </Typography>
              </Box>
            )}
          </PropertyListItem>
          <Divider />
          <PropertyListItem
            align={`horizontal`}
            label="รูปแบบการชำระเงิน"
          >
            {data?.paymentMethod === EOrderType.COD ? (
              <Typography variant="body2" color="text.secondary">
                เก็บเงินปลายทาง
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  โอนผ่านธนาคาร
                </Typography>
                {data?.paymentUrl && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ReceiptLongIcon sx={{ fontSize: 16 }} />}
                    onClick={() => setSlipOpen(true)}
                    sx={{ textTransform: 'none', borderRadius: 2, py: 0.25, fontSize: 13 }}
                  >
                    ดูสลิป
                  </Button>
                )}
              </Box>
            )}
          </PropertyListItem>
          <Divider />
          <PropertyListItem
            align={`horizontal`}
            label="	จำนวน"
            value={`${data?.items?.length ?? 0} รายการ ${data?.quantity} ชิ้น`}
          />
          <Divider />
          <PropertyListItem
            align={`horizontal`}
            label="สั่งซื้อเมื่อ"
            value={`${dayjs(data?.createdAt).format(DATE_FORMAT)}`}
          />
          <Divider />
          <PropertyListItem
            align={`horizontal`}
            label="ลงระบบย้อนหลัง"
            value={`${dayjs(data?.orderDate).format(DATE_FORMAT)}`}
          />
          <Divider />
          <PropertyListItem
            align={`horizontal`}
            label="ลงระบบโดย"
            value={data?.actor}
          />
          <Divider />
          {!isEmpty(data?.assignTelesaleBy) && (
            <>
              <PropertyListItem
                align={`horizontal`}
                label="ดูแล Telesale โดย"
                value={data?.assignTelesaleBy}
              />
              <Divider />
            </>
          )}
          <Divider />
          <PropertyListItem align={`horizontal`} label="ช่องทางการขาย">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {data?.distribution && (
                <Box sx={{ paddingTop: 1, mr: 1 }}>
                  <DistrubuteChannelTag distribution={data?.distribution} />
                </Box>
              )}
              {editable && (
                <Button
                  startIcon={<EditIcon />}
                  color="warning"
                  size="medium"
                  variant="text"
                  onClick={() =>
                    setIsModalEditDistribution(!isModalEditDistribution)
                  }
                >
                  แก้ไขช่องทางการขาย
                </Button>
              )}
            </Box>
          </PropertyListItem>
          <Divider />
          <PropertyListItem
            align={`horizontal`}
            label="หมายเหตุ"
            value={data?.note ? data?.note : <em>ไม่มีข้อมูล</em>}
          />
          <Divider />
        </PropertyList>
      </Card>

      <Dialog
        open={slipOpen}
        onClose={() => setSlipOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            หลักฐานการโอนเงิน
          </Typography>
          <IconButton size="small" onClick={() => setSlipOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', bgcolor: '#fafafa' }}>
          <Box
            component="img"
            src={data?.paymentUrl}
            alt="สลิปการโอนเงิน"
            sx={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: 1 }}
          />
        </Box>
      </Dialog>
    </>
  )
}

export default OrderDetailCard
